import { useEffect } from 'react'
import { useCallback } from 'react'
import { useState } from 'react'

import { connection as SOLANA_CONNECTION } from '@src/chains/solana/anchor/setup'
import { ChainId } from '@src/configs/chainInfo'
import { GasFeeMetrics } from '@src/types/gasFees'

export const getSolanaGasFeeMetrics = async () => {
    // Get a recent blockhash
    const latestBlockhash = await SOLANA_CONNECTION.getLatestBlockhash()

    // Solana fees are generally stable at ~5000 lamports per signature on mainnet.
    const feePerSignature = BigInt(5000)

    // Fetch minimum rent exemption for a typical account (e.g. a token account of 165 bytes)
    const minimumRentExemption = BigInt(await SOLANA_CONNECTION.getMinimumBalanceForRentExemption(165))

    const metrics: GasFeeMetrics = {
        chainId: ChainId.Solana,
        currentGasPrice: BigInt(0), // Not applicable for Solana
        isEIP1559: false,
        isTron: false,
        isSolana: true,
        SolanaParams: {
            feePerSignature,
            recentBlockhash: latestBlockhash.blockhash,
            minimumRentExemption,
        },
    }

    return metrics
}

export const useGasFeeMetricsSolana = () => {
    const [gasFeeMetrics, setGasMetrics] = useState<GasFeeMetrics | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const fetchSolanaGasMetrics = useCallback(async () => {
        setIsLoading(true)
        try {
            const metrics = await getSolanaGasFeeMetrics()
            setGasMetrics(metrics)
        } catch (err) {
            setError(`Error fetching Solana gas metrics: ${(err as Error).message}`)
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchSolanaGasMetrics()
    }, [fetchSolanaGasMetrics])

    return { gasFeeMetrics, error, isLoading }
}
